import { useState } from 'react';
import { subscribeNewsletter } from '../api/newsletter';
import { CountrySelect } from '../components/CountrySelect';
import { useCountryEstimate } from '../hooks/useCountryEstimate';
import { useRegions } from '../hooks/useRegions';

export function RegisterPage() {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [email, setEmail] = useState('');
  const [checked, setChecked] = useState(false);

  const regionsQuery = useRegions();
  const estimatedCountry = useCountryEstimate(regionsQuery.data);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  // 一度でも利用者が選んだら推定値は使わない。推定 (/geo) の応答が後から届いても
  // 利用者の選択を上書きしないため。
  const [isCountryTouched, setIsCountryTouched] = useState(false);
  const country = isCountryTouched ? selectedCountry : estimatedCountry;

  const handleCountryChange = (code: string) => {
    setSelectedCountry(code);
    setIsCountryTouched(true);
  };

  // 打ち始めた時点で、その時の表示 (推定値があればそれ) を確定させる。
  // 遅れて届いた推定値が入力中の欄を書き換えないため。
  const handleCountryInteract = () => {
    if (!isCountryTouched) {
      setSelectedCountry(estimatedCountry);
      setIsCountryTouched(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMsg('*Please enter a valid email address.');
      return;
    }
    if (!country) {
      return;
    }

    setIsLoading(true);
    try {
      await subscribeNewsletter(email, country);
      setIsSuccess(true);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Registration failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const isSubmitDisabled = !email || !country || !checked || isLoading;

  if (isSuccess) {
    return (
      <div className="main-container" id="subscription_confirm">
        <div className="main-container-top">
          <h2>Email Address Confirmation</h2>
          <p className="main-text">
            A subscription confirmation email has been sent. Please check your email.
          </p>
          <p className="note-text">
            Emails will be sent from info@newsletter.mangamillion.shueisha.co.jp.
            <br />
            If you do not receive an email, please check your spam or junk folder and add this
            address to your safe sender list if necessary.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container" id="subscription_form">
      <div className="main-container-top">
        <h2>Newsletter Subscription Form</h2>
        <p className="main-text">
          Please enter the email address where the newsletter should be sent and click the
          &quot;Subscribe&quot; button.
        </p>
      </div>
      <form className="form-fields" onSubmit={handleSubmit}>
        <div className="email-input">
          <label htmlFor="subscribeEmail">email</label>
          <input
            type="email"
            id="subscribeEmail"
            placeholder="Please input"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrorMsg('');
            }}
            required
          />
          {errorMsg && <p className="error-text">{errorMsg}</p>}
        </div>
        <CountrySelect
          id="residenceCountry"
          regions={regionsQuery.data ?? []}
          selectedCode={country}
          onChange={handleCountryChange}
          onInteract={handleCountryInteract}
          disabled={!regionsQuery.data}
        />
        {regionsQuery.isError && (
          <p className="error-text">Failed to load the list. Please try again later.</p>
        )}
        <label className="email-checkbox" htmlFor="ageVerification">
          <input
            type="checkbox"
            id="ageVerification"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <span className="main-text">I am 16 years of age or older.</span>
        </label>
        <button className="primary" type="submit" id="form-subscribe" disabled={isSubmitDisabled}>
          <span />
          <span>{isLoading ? 'Sending...' : 'Subscribe'}</span>
          <img src="/assets/arrow_right_white.svg" alt="" width="24" height="24" />
        </button>
      </form>
    </div>
  );
}
